import React from 'react';
import { View, ActivityIndicator, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { useArchiveRooms } from '../../hooks/useArchiveRooms';
import { ArchiveCard, type ArchiveCardRoom } from './ArchiveCard';
import type { RoomResponse } from '../../types/auth';

const RU_MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function formatDayMonthRu(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${RU_MONTHS_GENITIVE[d.getMonth()]}`;
}

/**
 * Map a backend RoomResponse into the ArchiveCardRoom shape the card
 * expects. Stats stay null at the list level (the immersive ended view
 * fetches them per-room via useRoomStats); recordingDurationSec is
 * derived from started→ended timestamps when both are present, else 0.
 */
export function mapRoomToArchiveCard(
  room: RoomResponse,
  currentUserId: string | null,
): ArchiveCardRoom {
  const startedAt = room.startedAtUtc ?? room.scheduledAtUtc ?? room.createdAt;
  const endedAt = room.endedAtUtc;
  const recordingDurationSec = startedAt && endedAt
    ? Math.max(
        0,
        Math.floor((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000),
      )
    : 0;
  return {
    id: room.id,
    title: room.title ?? room.videoPracticeTitle ?? 'Без названия',
    startDateTimeUtc: startedAt,
    durationMinutes: room.durationMinutes ?? room.videoPracticeDurationMinutes ?? 0,
    coverImageUrl: room.coverImageUrl ?? room.videoPracticeCoverImageUrl ?? null,
    masterName: room.hostName,
    recordingDurationSec,
    isOwn: currentUserId != null && room.hostUserId === currentUserId,
    isPartOfSeries: Boolean(room.isRecurring || room.parentRoomId),
    stats: { peakParticipants: null, viewCount: null, messageCount: null },
    access: room.access,
  };
}

interface Props {
  direction: string | null;
  date: string | null;
  currentUserId: string | null;
  onCardPress: (roomId: number) => void;
  /** See UpcomingTab — same web-only 4-col grid behaviour with the rail
   *  staying visible across loading / empty / populated states. */
  desktopRail?: React.ReactNode;
  /** SCRUM-49 — see UpcomingTab. Routes owner pencil taps to parent. */
  onOpenOwnerMenu?: (room: RoomResponse) => void;
}

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  columnGap: 12,
  rowGap: 12,
  alignItems: 'start',
} as const;

const RAIL_CELL_STYLE = {
  gridColumn: '4 / 5',
  gridRow: '1',
  position: 'sticky',
  top: 140,
  alignSelf: 'start',
} as const;

const SPAN_3_STYLE = { gridColumn: '1 / span 3', gridRow: '1' } as const;

const ArchiveEmpty: React.FC<{ date: string | null; isDark: boolean }> = ({ date, isDark }) => {
  const { Typography } = require('../ui/Typography');
  const titleCol = isDark ? '#F5F3F0' : '#0F1419';
  const mutedCol = isDark ? 'rgba(255,255,255,0.6)' : '#686170'; // CORE-1733: ≥4.5 на v4
  const iconCol = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,20,25,0.3)';

  return (
    <View style={{ paddingVertical: 56, paddingHorizontal: 24, alignItems: 'center', gap: 12 } as any}>
      <Ionicons name="film-outline" size={44} color={iconCol} />
      <Typography
        variant="h3"
        style={{ color: titleCol, textAlign: 'center' } as any}
      >
        {date
          ? `На ${formatDayMonthRu(date)} нет записей практик`
          : 'Записей пока нет'}
      </Typography>
      <Typography
        variant="body"
        style={{ color: mutedCol, textAlign: 'center', maxWidth: 380 } as any}
      >
        {date
          ? 'Попробуйте выбрать другой день в календаре или вернуться ко всем записям.'
          : 'Записи мастер-сессий появятся здесь после первых практик.'}
      </Typography>
    </View>
  );
};

export const ArchiveTab: React.FC<Props> = ({
  direction,
  date,
  currentUserId,
  onCardPress,
  desktopRail,
  onOpenOwnerMenu,
}) => {
  const { isDark } = useTheme();
  const { rooms, isLoading } = useArchiveRooms({ direction, date });

  if (desktopRail && Platform.OS === 'web') {
    return (
      <View style={GRID_STYLE as any}>
        <View style={RAIL_CELL_STYLE as any}>{desktopRail}</View>
        {isLoading ? (
          <View style={{ ...SPAN_3_STYLE, padding: 32, alignItems: 'center' } as any}>
            <ActivityIndicator color={isDark ? '#F4F4F5' : '#0F1419'} />
          </View>
        ) : rooms.length === 0 ? (
          <View style={SPAN_3_STYLE as any}>
            <ArchiveEmpty date={date} isDark={isDark} />
          </View>
        ) : (
          rooms.map((r) => (
            <View key={r.id} style={{ alignSelf: 'start' } as any}>
              <ArchiveCard
                room={mapRoomToArchiveCard(r, currentUserId)}
                onPress={() => onCardPress(r.id)}
                onOpenOwnerMenu={onOpenOwnerMenu ? () => onOpenOwnerMenu(r) : undefined}
              />
            </View>
          ))
        )}
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={{ padding: 32, alignItems: 'center' } as any}>
        <ActivityIndicator color={isDark ? '#F4F4F5' : '#0F1419'} />
      </View>
    );
  }

  if (rooms.length === 0) {
    return <ArchiveEmpty date={date} isDark={isDark} />;
  }

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 } as any}>
      {rooms.map((r) => (
        <View key={r.id} style={{ width: '33.3333%', paddingHorizontal: 6, paddingBottom: 12 } as any}>
          <ArchiveCard
            room={mapRoomToArchiveCard(r, currentUserId)}
            onPress={() => onCardPress(r.id)}
          />
        </View>
      ))}
    </View>
  );
};
